/**
 * 持仓 API — adapts Business Orders API to legacy Position shape
 * 下单自动确认见 frontend-api-integration.md §5.8
 */
import { getAccount } from '@/api/account';
import {
  createOrder,
  createOrderIdempotencyHeaders,
  getOrder,
  listOrders,
  previewOrder,
  submitOrderRequest,
  type CreateOrderRequest,
  type ListOrdersParams,
  type Order,
  type OrderPreview,
  type OrderStatus,
} from '@/api/orders';

/** §5.7 订单详情；自动确认 ACCEPTED 后按 id 刷新展示 */
export { getOrder } from '@/api/orders';
import { wattToMinUnit, minUnitToWatt } from '@/common/utils/money';
import { assertRolling60sSettleWindow } from '@/common/utils/serverClock';
import {
  clearPendingOrderRecovery,
  hasPendingOrderRecovery,
  loadPendingOrderRecovery,
  savePendingOrderRecovery,
  shouldClearOrderRecoveryOnError,
} from '@/common/utils/orderRecovery';
import { ensureOrderSubmissionTicket } from '@/common/services/orderSubmissionTicket';
import { clearOrderPreviews, rememberConversionPreview, takeOrderPreview } from '@/common/services/orderPreview';
import { ApiError, getCurrentWalletAddress, ensureAuthenticated, getToken } from '@/common/services/request';
import {
  AI_PREDICTION_ALLOWED_API_INTERVAL,
  resolveAiPredictionSymbol,
  isOrderTimeframeSupported,
  isRollingOrderInterval,
} from '@/common/constants';
import type {
  Position,
  PositionDirection,
  PositionStatus,
  CreatePositionRequest,
  CreatePositionResponse,
  PositionsResponse,
  TimeFrame,
} from '@/types/position';

const ACTIVE_ORDER_STATUSES: OrderStatus[] = [
  'CREATED',
  'ACCEPTED',
  'FUNDS_LOCKED',
  'AWAITING_SETTLEMENT',
  'PROCESSING',
];

const SETTLED_ORDER_STATUSES: OrderStatus[] = ['WON', 'LOST', 'REFUNDED'];

/** 串行：正式下单与 resolve 互斥，避免并发 prepare/resolve */
let orderPipeline: Promise<unknown> = Promise.resolve();

function runOrderPipeline<T>(fn: () => Promise<T>): Promise<T> {
  const next = orderPipeline.then(fn, fn);
  orderPipeline = next.then(
    () => undefined,
    () => undefined
  );
  return next;
}

function assertSameWallet(expected: string): void {
  const current = (getCurrentWalletAddress() || '').toLowerCase().trim();
  const want = (expected || '').toLowerCase().trim();
  if (!want || !current || current !== want) {
    throw new ApiError('Wallet switched during order submit', {
      code: 'WALLET_SWITCHED',
      statusCode: 0,
    });
  }
}

/** Map Order.id (string UUID or numeric) → Position.id number */
function orderIdToNumber(id: string): number {
  const trimmed = String(id ?? '').trim();
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isSafeInteger(n)) return n;
  }
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = (hash << 5) - hash + trimmed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
}

/** 列表去重 / 结算写回：UUID 与本地数字 id（由 UUID 哈希）视为同一笔 */
export function samePositionOrder(
  a: { orderId?: string | null; id?: number | string },
  b: { orderId?: string | null; id?: number | string }
): boolean {
  const aOid = String(a.orderId || '').trim();
  const bOid = String(b.orderId || '').trim();
  if (aOid && bOid) return aOid === bOid;
  if (aOid && String(b.id) === String(orderIdToNumber(aOid))) return true;
  if (bOid && String(a.id) === String(orderIdToNumber(bOid))) return true;
  const aId = String(a.id ?? '').trim();
  const bId = String(b.id ?? '').trim();
  return !!aId && aId === bId;
}

export function positionOrderKey(p: {
  orderId?: string | null;
  id?: number | string;
}): string {
  const oid = String(p.orderId || '').trim();
  if (oid) return oid;
  return String(p.id || '');
}

function directionToOrder(direction: PositionDirection): 'RISE' | 'FALL' {
  return direction === 'LONG' ? 'RISE' : 'FALL';
}

function directionFromOrder(direction: Order['direction']): PositionDirection {
  return direction === 'RISE' ? 'LONG' : 'SHORT';
}

function statusFromOrder(status: OrderStatus): PositionStatus {
  if (status === 'CLOSED') {
    // 未成交关闭不应进当前持仓展示
    return 'SETTLED';
  }
  if (ACTIVE_ORDER_STATUSES.includes(status)) return 'ACTIVE';
  if (status === 'MANUAL_REVIEW') return 'PENDING_SETTLEMENT';
  if (SETTLED_ORDER_STATUSES.includes(status)) return 'SETTLED';
  // 后端新增或未知状态不能误显示为已结算历史。
  return 'ACTIVE';
}

/** UI timeframe → 接口 interval；不得把长周期静默回落成 1m（§9.1.1） */
function timeframeToInterval(timeframe?: TimeFrame | string): string {
  if (!timeframe || timeframe === '1s' || timeframe === '1m') return '1m';
  const value = String(timeframe).toLowerCase().trim();
  if (isRollingOrderInterval(value)) return value;
  throw new ApiError('非法合约周期', {
    code: 'ORDER_MODE_PARAMETERS_INVALID',
    statusCode: 400,
  });
}

function normalizeTradeSymbol(symbol: string): string {
  const s = String(symbol || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (s === 'BTCUSDT' || s.startsWith('BTC')) return 'BTCUSDT';
  if (s === 'ETHUSDT' || s.startsWith('ETH')) return 'ETHUSDT';
  return s || 'BTCUSDT';
}

/** 接口 interval → UI timeframe；历史缺失不伪造 1m（§6.1 / §9.1.1） */
function intervalToTimeframe(interval?: string): TimeFrame {
  const value = String(interval || '').toLowerCase().trim();
  if (!value) return '1m';
  if (value === '1s' || value === '1m') return '1m';
  if (isRollingOrderInterval(value)) return value as TimeFrame;
  // 未知历史值原样保留，供页面显示「未记录」类处理，不补成 1m
  return value as TimeFrame;
}

function orderModeFromType(
  type: 0 | 1 | undefined,
  params: CreatePositionRequest & {
    aiPredictionId?: string;
    insuranceEnabled?: boolean;
    insuranceSessionId?: string;
  }
): string {
  if (
    type === 1 &&
    params.insuranceEnabled &&
    (params.insuranceSessionId || params.insuranceTierSelection) &&
    params.aiPredictionId
  ) {
    return 'AI_INSURED_ROLLING_60S';
  }
  return 'ORDINARY_ROLLING_60S';
}

function positionTypeFromOrder(order: Order): 0 | 1 {
  const source = String(order.orderSource || '').toUpperCase();
  if (source === 'AI') return 1;
  // ORDINARY / COPY 均归普通事件合约页
  if (source === 'ORDINARY' || source === 'COPY') return 0;
  return 0;
}

function stakeToString(stake: string | number): string {
  return String(stake ?? '0');
}

export function mapOrderToPosition(order: Order): Position {
  assertRolling60sSettleWindow(order, order);

  const stakeStr = stakeToString(order.stake);
  let amount = '0';
  try {
    amount = minUnitToWatt(stakeStr);
  } catch {
    amount = '0';
  }

  let profitLoss: string | null = null;
  if (order.netProfit != null && order.netProfit !== '') {
    try {
      // §5.7：netProfit 带符号，亏损必须保留负号
      const raw = String(order.netProfit).trim();
      const neg = raw.startsWith('-');
      const abs = neg ? raw.slice(1) : raw;
      const human = minUnitToWatt(abs);
      profitLoss = neg ? `-${human}` : human;
    } catch {
      profitLoss = null;
    }
  }

  return {
    id: orderIdToNumber(order.id || order.orderId),
    orderId: String(order.id || order.orderId || '').trim() || null,
    symbol: order.symbol,
    direction: directionFromOrder(order.direction),
    open_price: order.openPrice != null ? String(order.openPrice) : '0',
    close_price: order.closePrice != null ? String(order.closePrice) : null,
    amount,
    token_symbol: 'WATT',
    timeframe: intervalToTimeframe(order.interval),
    status: statusFromOrder(order.status),
    order_status: order.status || null,
    type: positionTypeFromOrder(order),
    created_at: order.createdAt || order.acceptedAt,
    settlement_time: order.settleAt || order.acceptedAt,
    settled_at: order.settledAt ?? null,
    profit_loss: profitLoss,
    insurance_enabled:
      order.effectiveInsuranceType === 'EVENT' ||
      order.effectiveInsuranceType === 'AI_PREDICTION' ||
      order.insuranceEnabled === true,
    insurance_effective:
      order.effectiveInsuranceType === 'EVENT' ||
      order.effectiveInsuranceType === 'AI_PREDICTION' ||
      order.insuranceEnabled === true,
    insurance_status:
      order.effectiveInsuranceType ||
      (order.insuranceEnabled === true ? 'active' : 'unselected'),
    effective_insurance_type: order.effectiveInsuranceType ?? null,
    ai_insurance_converted: order.aiInsuranceConverted === true,
    prediction_direction: order.predictionDirection ?? null,
    order_source: order.orderSource ?? null,
    ai_prediction_id: order.aiPredictionId ?? null,
    premium: (() => {
      try {
        if (order.premium == null || String(order.premium).trim() === '') return null;
        return minUnitToWatt(String(order.premium));
      } catch {
        return null;
      }
    })(),
  };
}

function mapOrderToCreateResponse(order: Order): CreatePositionResponse {
  const pos = mapOrderToPosition(order);
  return {
    id: pos.id,
    orderId: pos.orderId ?? null,
    symbol: pos.symbol,
    direction: pos.direction,
    open_price: pos.open_price,
    amount: pos.amount,
    token_symbol: pos.token_symbol,
    timeframe: pos.timeframe,
    status: pos.status,
    order_status: pos.order_status,
    created_at: pos.created_at,
    settlement_time: pos.settlement_time,
    insurance_enabled: pos.insurance_enabled ?? null,
    insurance_effective: pos.insurance_effective ?? null,
    insurance_status: pos.insurance_status ?? null,
    effective_insurance_type: pos.effective_insurance_type ?? null,
    ai_insurance_converted: pos.ai_insurance_converted ?? null,
    prediction_direction: pos.prediction_direction ?? null,
    order_source: pos.order_source ?? null,
    ai_prediction_id: pos.ai_prediction_id ?? null,
    premium: pos.premium ?? null,
  };
}

function orderNotFilledError(): ApiError {
  return new ApiError('本次下单未成交，未扣款', {
    code: 'ORDER_NOT_FILLED',
    statusCode: 0,
  });
}

function confirmingError(): ApiError {
  return new ApiError('正在确认下单结果，请勿重复操作', {
    code: 'ORDER_CONFIRMING',
    statusCode: 0,
  });
}

export type CreatePositionParams = CreatePositionRequest & {
  aiPredictionId?: string;
  insuranceEnabled?: boolean;
  insuranceSessionId?: string;
  terminateInsuranceSession?: boolean;
  /** Joule8 字符串；优先于 amount 的可读换算 */
  stakeMinUnit?: string;
};

/** One canonical body for background previews and the final click. No client price. */
export function buildPositionOrderRequest(params: CreatePositionParams): CreateOrderRequest {
  const type = params.type ?? 0;
  const aiLinked = type === 1 || !!params.aiPredictionId;
  // AI 关联 / AI 保险仅 1m；长周期不可自动降级（§2.4 / §8）
  if (aiLinked) {
    const tf = String(params.timeframe || '').toLowerCase().trim();
    if (tf && tf !== '1m' && tf !== '1s') {
      throw new ApiError('AI 关联订单仅支持 1 分钟周期', {
        code: 'ORDER_MODE_PARAMETERS_INVALID',
        statusCode: 400,
      });
    }
  } else if (params.timeframe && !isOrderTimeframeSupported(params.timeframe)) {
    throw new ApiError('该周期正在开发中', { code: 'TIMEFRAME_UNDER_DEVELOPMENT', statusCode: 0 });
  }
  const orderMode = orderModeFromType(type, params);
  const body: CreateOrderRequest = {
    symbol: type === 1
      ? (resolveAiPredictionSymbol(params.symbol) || normalizeTradeSymbol(params.symbol))
      : normalizeTradeSymbol(params.symbol),
    interval: aiLinked ? AI_PREDICTION_ALLOWED_API_INTERVAL : timeframeToInterval(params.timeframe),
    direction: directionToOrder(params.direction),
    stake: params.stakeMinUnit != null && String(params.stakeMinUnit).trim() !== ''
      ? String(params.stakeMinUnit).trim() : wattToMinUnit(String(params.amount)),
    orderMode,
  };
  if (params.aiPredictionId) body.aiPredictionId = params.aiPredictionId;
  if (orderMode === 'AI_INSURED_ROLLING_60S') {
    body.insuranceEnabled = true;
    if (params.insuranceTierSelection != null) body.insuranceTierSelection = params.insuranceTierSelection;
    if (params.insuranceSessionId) body.insuranceSessionId = params.insuranceSessionId;
  } else if (params.insuranceEnabled === true) {
    body.insuranceEnabled = true;
    body.insuranceProduct = 'EVENT';
  } else if (params.insuranceEnabled === false) {
    body.insuranceEnabled = false;
  }
  return body;
}

export interface CreatePositionOptions {
  /**
   * @deprecated §5.8 不再重发旧订单；请用 confirmPendingOrderSubmission
   */
  recover?: boolean;
  /** 投保预览成功后、提交前。返回 false 则取消，不把本地保费当成交依据。 */
  onInsurancePreview?: (preview: OrderPreview) => Promise<boolean>;
}

export type ConfirmPendingOutcome =
  | { outcome: 'none' }
  | { outcome: 'accepted'; position: CreatePositionResponse; order: Order }
  | { outcome: 'closed' }
  | { outcome: 'pending' }
  | { outcome: 'aborted'; reason: string };

/**
 * §5.8 resolve：查询成交结果，不重发 POST /orders
 */
export const confirmPendingOrderSubmission = (
  wallet?: string
): Promise<ConfirmPendingOutcome> => {
  return runOrderPipeline(async () => {
    const pending = loadPendingOrderRecovery(wallet);
    if (!pending) return { outcome: 'none' };

    const addr = pending.walletAddress;
    try {
      assertSameWallet(addr);
    } catch {
      return { outcome: 'aborted', reason: 'WALLET_MISMATCH' };
    }

    try {
      const submission = await submitOrderRequest(
        {
          clientOrderId: pending.clientOrderId,
          idempotencyKey: pending.idempotencyKey,
          action: 'resolve',
        },
        { expectedWallet: addr }
      );

      if (submission.status === 'ACCEPTED' && submission.orderId) {
        const order = await getOrder(submission.orderId, {
          expectedWallet: addr,
        });
        clearPendingOrderRecovery(addr);
        return {
          outcome: 'accepted',
          position: mapOrderToCreateResponse(order),
          order,
        };
      }

      if (submission.status === 'CLOSED') {
        clearPendingOrderRecovery(addr);
        return { outcome: 'closed' };
      }

      // READY 等：继续保留，下次再确认
      return { outcome: 'pending' };
    } catch (err) {
      if (shouldClearOrderRecoveryOnError(err)) {
        clearPendingOrderRecovery(addr);
        throw err;
      }
      return { outcome: 'pending' };
    }
  });
};

export { hasPendingOrderRecovery };

/**
 * 创建持仓 →（投保 preview）→ 持久化 → direct-v1 单请求 / 回退 prepare → POST /orders
 * 结果不明时保留 pending，由 confirmPendingOrderSubmission / App 自动 resolve。
 */
export const createPosition = async (
  params: CreatePositionParams,
  options: CreatePositionOptions = {}
): Promise<CreatePositionResponse> => {
  return runOrderPipeline(async () => {
    const walletAddress = (getCurrentWalletAddress() || '').toLowerCase().trim();
    if (!walletAddress) {
      throw new ApiError('Wallet not connected', {
        code: 'WALLET_REQUIRED',
        statusCode: 0,
      });
    }

    // 已有待确认记录：禁止另开新单，也不重发旧单
    if (hasPendingOrderRecovery(walletAddress)) {
      throw confirmingError();
    }

    const tokenBefore = getToken();
    await ensureAuthenticated(walletAddress);
    assertSameWallet(walletAddress);
    if (getToken() !== tokenBefore) clearOrderPreviews();

    const type = params.type ?? 0;
    const body = buildPositionOrderRequest(params);
    const orderMode = body.orderMode;
    const needsInsurancePreview =
      orderMode === 'AI_INSURED_ROLLING_60S' || params.insuranceEnabled === true;
    const needsPreview = needsInsurancePreview || !!params.aiPredictionId;

    assertSameWallet(walletAddress);

    // 投保必须 preview；普通不投保可跳过（有有效 ticket 时）
    let preview: OrderPreview | null = null;
    if (needsPreview) {
      const previewStarted = performance.now();
      preview = takeOrderPreview(body, walletAddress)
        ?? await previewOrder(body, { expectedWallet: walletAddress });
      assertSameWallet(walletAddress);

      // 预览与提交周期必须一致；切换周期后旧报价不可用（§4.2）
      if (
        preview?.interval != null &&
        String(preview.interval).trim() !== '' &&
        String(preview.interval).trim().toLowerCase() !== String(body.interval).trim().toLowerCase()
      ) {
        throw new ApiError('预览周期与下单周期不一致，请重新确认', {
          code: 'ORDER_MODE_PARAMETERS_INVALID',
          statusCode: 400,
        });
      }

      if (preview?.funding && preview.funding.canSubmit === false) {
        const premiumShort = preview.funding.premiumShortfall;
        const err = new ApiError(
          premiumShort && String(premiumShort) !== '0'
            ? 'Insurance wallet balance insufficient'
            : 'Insufficient main balance',
          {
            code:
              premiumShort && String(premiumShort) !== '0'
                ? 'INSURANCE_BALANCE_INSUFFICIENT'
                : 'INSUFFICIENT_FUNDS',
            statusCode: 400,
          }
        ) as ApiError & { preview?: OrderPreview };
        err.preview = preview;
        throw err;
      }
      if (preview?.insuranceFundingQuoteId) {
        body.insuranceFundingQuoteId = preview.insuranceFundingQuoteId;
      }
      // AI 方向/仓位错单：须用户明确确认后再带转换凭证提交（§15.1）
      if (preview?.endsInsuranceSession) {
        if (!params.terminateInsuranceSession) {
          rememberConversionPreview(buildPositionOrderRequest(params), walletAddress, preview, performance.now() - previewStarted);
          const err = new ApiError(
            'AI direction/stake mismatch requires conversion confirm',
            {
              code: 'INSURANCE_CONVERSION_REQUIRED',
              statusCode: 400,
            }
          ) as ApiError & { preview?: OrderPreview };
          err.preview = preview;
          throw err;
        }
        if (preview.insuranceConversionQuoteId) {
          body.insuranceConversionQuoteId = preview.insuranceConversionQuoteId;
          body.terminateInsuranceSession = true;
        }
      } else if (
        params.terminateInsuranceSession &&
        preview?.insuranceConversionQuoteId
      ) {
        body.insuranceConversionQuoteId = preview.insuranceConversionQuoteId;
        body.terminateInsuranceSession = true;
      }
    }

    const insuredOrder =
      orderMode === 'AI_INSURED_ROLLING_60S' || params.insuranceEnabled === true;
    if (insuredOrder && preview && options.onInsurancePreview) {
      const ok = await options.onInsurancePreview(preview);
      if (!ok) {
        throw new ApiError('Insurance preview cancelled', {
          code: 'INSURANCE_PREVIEW_CANCELLED',
          statusCode: 0,
        });
      }
    }

    assertSameWallet(walletAddress);

    // No leftover speculative quote can survive an actual submission attempt.
    clearOrderPreviews();

    // 优先使用预览附带凭证，否则领取 ticket；null → prepare 回退
    let ticket =
      preview?.submission?.token && preview.submission.mode === 'direct-v1'
        ? preview.submission
        : await ensureOrderSubmissionTicket();

    if (ticket && ticket.mode !== 'direct-v1') {
      ticket = null;
    }

    const headers = createOrderIdempotencyHeaders(
      type === 1 ? 'ai-rolling' : 'rolling'
    );

    const saved = savePendingOrderRecovery({
      walletAddress,
      idempotencyKey: headers.idempotencyKey,
      clientOrderId: headers.clientOrderId,
      body,
      createdAt: Date.now(),
      submissionMode: ticket?.mode,
      submissionToken: ticket?.token,
      submissionExpiresAt: ticket?.expiresAt,
      meta: {
        direction: params.direction,
        amountHuman: String(params.amount),
        symbol: body.symbol,
        type,
      },
    });
    if (!saved) {
      throw new ApiError('Failed to persist pending order request', {
        code: 'PENDING_ORDER_SAVE_FAILED',
        statusCode: 0,
      });
    }

    const tryResolveAfterUncertain = async (): Promise<CreatePositionResponse> => {
      const pending = loadPendingOrderRecovery(walletAddress);
      if (!pending) throw confirmingError();

      try {
        assertSameWallet(walletAddress);
        const submission = await submitOrderRequest(
          {
            clientOrderId: pending.clientOrderId,
            idempotencyKey: pending.idempotencyKey,
            action: 'resolve',
          },
          { expectedWallet: walletAddress }
        );

        if (
          'status' in submission &&
          submission.status === 'ACCEPTED' &&
          submission.orderId
        ) {
          const order = await getOrder(submission.orderId, {
            expectedWallet: walletAddress,
          });
          clearPendingOrderRecovery(walletAddress);
          return mapOrderToCreateResponse(order);
        }
        if ('status' in submission && submission.status === 'CLOSED') {
          clearPendingOrderRecovery(walletAddress);
          throw orderNotFilledError();
        }
      } catch (resolveErr) {
        if (shouldClearOrderRecoveryOnError(resolveErr)) {
          clearPendingOrderRecovery(walletAddress);
          throw resolveErr;
        }
        if (
          resolveErr instanceof ApiError &&
          resolveErr.code === 'ORDER_NOT_FILLED'
        ) {
          throw resolveErr;
        }
      }
      throw confirmingError();
    };

    try {
      assertSameWallet(walletAddress);

      // —— direct-v1：有有效 ticket 时跳过 prepare，直接 POST /orders ——
      if (ticket?.token) {
        try {
          const order = await createOrder(body, {
            idempotencyKey: headers.idempotencyKey,
            clientOrderId: headers.clientOrderId,
            orderSubmissionMode: 'direct-v1',
            orderSubmissionToken: ticket.token,
            expectedWallet: walletAddress,
            authRetryLimit: 0,
          });
          clearPendingOrderRecovery(walletAddress);
          return mapOrderToCreateResponse(order);
        } catch (err) {
          const code = String((err as ApiError)?.code || '').toUpperCase();
          // 凭证过期/关闭：核实原请求，不换凭证重发旧单
          if (code === 'ORDER_SUBMISSION_CLOSED' || code === 'ORDER_MODE_PARAMETERS_INVALID') {
            try {
              return await tryResolveAfterUncertain();
            } catch (resolveErr) {
              if (
                resolveErr instanceof ApiError &&
                resolveErr.code === 'ORDER_CONFIRMING'
              ) {
                throw err;
              }
              throw resolveErr;
            }
          }
          if (shouldClearOrderRecoveryOnError(err)) {
            clearPendingOrderRecovery(walletAddress);
            throw err;
          }
          try {
            return await tryResolveAfterUncertain();
          } catch (resolveErr) {
            const closed =
              resolveErr instanceof ApiError &&
              resolveErr.code === 'ORDER_NOT_FILLED';
            if (closed && err instanceof ApiError) {
              const st = Number(err.statusCode || 0);
              const uncertainAuth =
                st === 401 || st === 403 || st === 408 || st === 429;
              if (st >= 400 && st < 500 && !uncertainAuth) {
                clearPendingOrderRecovery(walletAddress);
                throw err;
              }
            }
            throw resolveErr;
          }
        }
      }

      // —— 回退：签名未配置 / 无 ticket → 旧 prepare 流程 ——
      let submission;
      try {
        submission = await submitOrderRequest(
          {
            clientOrderId: headers.clientOrderId,
            idempotencyKey: headers.idempotencyKey,
            action: 'prepare',
          },
          { expectedWallet: walletAddress }
        );
      } catch {
        return await tryResolveAfterUncertain();
      }

      assertSameWallet(walletAddress);

      if (
        'status' in submission &&
        submission.status === 'ACCEPTED' &&
        submission.orderId
      ) {
        const order = await getOrder(submission.orderId, {
          expectedWallet: walletAddress,
        });
        clearPendingOrderRecovery(walletAddress);
        return mapOrderToCreateResponse(order);
      }

      if ('status' in submission && submission.status === 'CLOSED') {
        clearPendingOrderRecovery(walletAddress);
        throw orderNotFilledError();
      }

      if (!('status' in submission) || submission.status !== 'READY') {
        return await tryResolveAfterUncertain();
      }

      try {
        const order = await createOrder(body, {
          idempotencyKey: headers.idempotencyKey,
          clientOrderId: headers.clientOrderId,
          expectedWallet: walletAddress,
          authRetryLimit: 0,
        });
        clearPendingOrderRecovery(walletAddress);
        return mapOrderToCreateResponse(order);
      } catch (err) {
        if (shouldClearOrderRecoveryOnError(err)) {
          clearPendingOrderRecovery(walletAddress);
          throw err;
        }
        try {
          return await tryResolveAfterUncertain();
        } catch (resolveErr) {
          const closed =
            resolveErr instanceof ApiError &&
            resolveErr.code === 'ORDER_NOT_FILLED';
          if (closed && err instanceof ApiError) {
            const st = Number(err.statusCode || 0);
            const uncertainAuth =
              st === 401 || st === 403 || st === 408 || st === 429;
            if (st >= 400 && st < 500 && !uncertainAuth) {
              clearPendingOrderRecovery(walletAddress);
              throw err;
            }
            if (
              err.message &&
              err.code &&
              !/TIMEOUT|NETWORK|UNKNOWN|UNAUTHORIZED|ORDER_CONFIRMING/i.test(
                String(err.code)
              )
            ) {
              clearPendingOrderRecovery(walletAddress);
              throw err;
            }
          }
          throw resolveErr;
        }
      }
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === 'ORDER_CONFIRMING' ||
          err.code === 'ORDER_NOT_FILLED' ||
          err.code === 'WALLET_SWITCHED' ||
          err.code === 'PENDING_ORDER_SAVE_FAILED')
      ) {
        throw err;
      }
      if (shouldClearOrderRecoveryOnError(err)) {
        clearPendingOrderRecovery(walletAddress);
      }
      throw err;
    }
  });
};

/**
 * 获取用户持仓 / 历史 → GET /v1/orders
 * CURRENT：未到期，两页共享，不传 orderSource / includeCopy。
 * HISTORY：到期未结 + 已完成；用 orderSource 区分页面。
 * 普通历史（ORDINARY）须带 includeCopy=true，后端一并返回 COPY 跟单单。
 * AI 历史不要传 includeCopy。
 */
export const getUserPositions = async (
  status?: string,
  type?: 0 | 1,
  options: {
    limit?: number;
    cursor?: string | null;
    expectedWallet?: string;
    orderSource?: 'ORDINARY' | 'AI';
    /** 仅普通 HISTORY 使用；默认在 orderSource=ORDINARY 时自动为 true */
    includeCopy?: boolean;
  } = {}
): Promise<PositionsResponse> => {
  const limit = options.limit ?? 10;
  const cursor = options.cursor ?? null;
  const expectedWallet =
    options.expectedWallet ||
    (getCurrentWalletAddress() || '').toLowerCase().trim() ||
    undefined;

  const listStatus = String(status || '').toUpperCase();
  const params: ListOrdersParams = { limit };
  if (cursor) params.cursor = cursor;
  if (listStatus) params.status = listStatus;
  if (listStatus === 'HISTORY') {
    const source =
      options.orderSource || (type === 1 ? 'AI' : type === 0 ? 'ORDINARY' : undefined);
    if (source) params.orderSource = source;
    // 普通事件合约历史：首次 / 刷新 / 分页均带 includeCopy，拉取普通单+跟单单
    if (
      String(source || '').toUpperCase() === 'ORDINARY' &&
      options.includeCopy !== false
    ) {
      params.includeCopy = true;
    }
  }

  const page = await listOrders(params, { expectedWallet });
  const items = page?.items || [];
  const positions = (
    listStatus === 'CURRENT' || listStatus === 'ACTIVE'
      ? items.filter((o) => String(o.status || '').toUpperCase() !== 'CLOSED')
      : items
  ).map(mapOrderToPosition);
  const nextCursor =
    page?.nextCursor && String(page.nextCursor).length > 0
      ? String(page.nextCursor)
      : null;

  return {
    positions,
    total: positions.length,
    nextCursor,
  };
};

/**
 * 获取平台币可用余额（WATT，人类可读）→ GET /v1/account
 */
export const getAmaBalance = async (): Promise<string> => {
  try {
    const account = await getAccount();
    return minUnitToWatt(String(account.available ?? '0'));
  } catch (error) {
    console.error('获取 WATT 余额失败:', error);
    return '0';
  }
};
